import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { PageHeader, Empty } from "@/components/ui-helpers";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, TextIn, Toggle } from "@/components/forms/Field";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, fmtRel } from "@/lib/fmt";
import { Plus, Play, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const BLANK = { name: "", prompt: "", skills: [], model: "", schedule: { kind: "daily", time: "09:00", weekday: 0 }, include_crm_context: true, post_to_slack: true, enabled: true };
const schedLabel = (s) => (s?.kind === "hourly" ? "Every hour" : s?.kind === "daily" ? `Daily ${s.time} IST` : s?.kind === "weekly" ? `${DAYS[s.weekday || 0]} ${s.time} IST` : "Manual only");

function TaskDialog({ task, onClose, onSaved, skills, models }) {
  const [t, setT] = useState(task);
  const set = (p) => setT((x) => ({ ...x, ...p }));
  const setS = (p) => set({ schedule: { ...t.schedule, ...p } });
  const has = (id) => t.skills.some((s) => s.skill_id === id);
  const toggle = (s) => set({ skills: has(s.skill_id) ? t.skills.filter((x) => x.skill_id !== s.skill_id) : [...t.skills, { skill_id: s.skill_id, type: s.type }] });
  const save = async () => {
    if (!t.name.trim() || !t.prompt.trim()) { toast.error("Name and instructions are required"); return; }
    try {
      const body = { ...t, model: t.model || undefined };
      t._id ? await api.put(`/claude/tasks/${t._id}`, body) : await api.post("/claude/tasks", body);
      toast.success("Task saved"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const sel = "w-full px-2.5 py-1.5 rounded border text-sm";
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[var(--panel)] border-[var(--border-strong)] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="claude-task-dialog">
        <DialogHeader><DialogTitle className="text-base font-medium">{t._id ? "Edit task" : "New Claude task"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Name"><TextIn value={t.name} onChange={(v) => set({ name: v })} testid="task-name" /></Field>
          <Field label="Instructions for Claude">
            <textarea rows={6} className={sel} value={t.prompt} onChange={(e) => set({ prompt: e.target.value })} data-testid="task-prompt" placeholder="e.g. Review today's pipeline and list the 5 leads most likely to close, with a next step for each." />
          </Field>
          <Field label="Skills from your Anthropic console" hint="Your own uploaded skills appear here automatically.">
            <div className="flex flex-wrap gap-1.5" data-testid="task-skills">
              {skills.map((s) => (
                <button key={s.skill_id} type="button" onClick={() => toggle(s)} data-testid={`skill-${s.skill_id}`}
                  className={`px-2 py-1 rounded border text-xs ${has(s.skill_id) ? "bg-white text-black border-white" : "text-neutral-400"}`}>
                  {s.title}<span className="opacity-50 ml-1">{s.type}</span></button>
              ))}
              {!skills.length && <span className="text-xs text-neutral-600">No skills found.</span>}
            </div>
          </Field>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <Field label="Model">
              <select className={sel} value={t.model || ""} onChange={(e) => set({ model: e.target.value })} data-testid="task-model">
                <option value="">Default (Sonnet 5.5)</option>{models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
              </select>
            </Field>
            <Field label="Schedule">
              <select className={sel} value={t.schedule.kind} onChange={(e) => setS({ kind: e.target.value })} data-testid="task-schedule">
                <option value="manual">Manual only</option><option value="hourly">Hourly</option><option value="daily">Daily</option><option value="weekly">Weekly</option>
              </select>
            </Field>
            {["daily", "weekly"].includes(t.schedule.kind) && (
              <Field label="Time (IST)"><TextIn type="time" value={t.schedule.time} onChange={(v) => setS({ time: v })} testid="task-time" /></Field>
            )}
          </div>
          {t.schedule.kind === "weekly" && (
            <div className="flex gap-1">{DAYS.map((d, i) => (
              <button key={d} type="button" onClick={() => setS({ weekday: i })} data-testid={`task-day-${i}`}
                className={`px-2 py-1 text-[11px] rounded border ${t.schedule.weekday === i ? "bg-white text-black border-white" : "text-neutral-400"}`}>{d}</button>
            ))}</div>
          )}
          <div className="flex flex-wrap gap-4">
            <Toggle checked={t.include_crm_context} onChange={(v) => set({ include_crm_context: v })} label="Attach live CRM snapshot" testid="task-crm-context" />
            <Toggle checked={t.post_to_slack} onChange={(v) => set({ post_to_slack: v })} label="Post result to Slack" testid="task-slack" />
            <Toggle checked={t.enabled} onChange={(v) => set({ enabled: v })} label="Enabled" testid="task-enabled" />
          </div>
          <button className="btn btn-primary w-full justify-center" onClick={save} data-testid="task-save">Save task</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function ClaudeRuns() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [runs, setRuns] = useState([]);
  const [skills, setSkills] = useState([]);
  const [models, setModels] = useState([]);
  const [edit, setEdit] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const { confirm } = useConfirm();
  const load = useCallback(() => {
    api.get("/claude/tasks").then((r) => setTasks(r.data.rows));
    api.get("/claude/runs").then((r) => setRuns(r.data.rows));
  }, []);
  useEffect(() => {
    api.get("/claude/status").then((r) => setStatus(r.data));
    api.get("/claude/skills").then((r) => setSkills(r.data.rows)).catch(() => {});
    api.get("/claude/models").then((r) => setModels(r.data.rows)).catch(() => {});
    load();
  }, [load]);
  useEffect(() => {
    if (!runs.some((r) => r.status === "running")) return undefined;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [runs, load]);

  const run = async (t) => { await api.post(`/claude/tasks/${t._id}/run`); toast.success(`Running “${t.name}”…`); setTimeout(load, 800); };
  const del = async (t) => { if (!(await confirm({ title: `Delete “${t.name}”?`, body: "Its run history is kept.", danger: true }))) return; await api.delete(`/claude/tasks/${t._id}`); load(); };

  return (
    <div data-testid="claude-page">
      <PageHeader title="Claude" sub={status?.configured ? `Anthropic key ${status.key_hint} · scheduled tasks run every 15 min check` : "Anthropic key not configured"}
        right={isAdmin && <button className="btn btn-primary" onClick={() => setEdit({ ...BLANK })} data-testid="new-claude-task-btn"><Plus size={14} />Task</button>} />
      <div className="p-4 md:p-6 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)] gap-6">
        <section>
          <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Tasks</h3>
          {!tasks.length ? <Empty label="No tasks yet — add one and pick your skills" /> : (
            <div className="panel divide-y divide-[var(--border)]" data-testid="claude-tasks">
              {tasks.map((t) => (
                <div key={t._id} className="px-4 py-3" data-testid={`claude-task-${t._id}`}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium flex-1 truncate">{t.name}</span>
                    {!t.enabled && <span className="pill">paused</span>}
                    <button className="btn text-xs" onClick={() => run(t)} data-testid={`task-run-${t._id}`}><Play size={12} />Run now</button>
                    {isAdmin && <button className="btn-ghost p-1" onClick={() => setEdit({ ...BLANK, ...t, model: t.model || "" })} data-testid={`task-edit-${t._id}`}><Pencil size={13} /></button>}
                    {isAdmin && <button className="btn-ghost p-1 hover:text-[var(--breach)]" onClick={() => del(t)} data-testid={`task-del-${t._id}`}><Trash2 size={13} /></button>}
                  </div>
                  <div className="text-[11px] text-neutral-500 mt-1">{schedLabel(t.schedule)} · {t.skills?.length || 0} skills{t.post_to_slack ? " · Slack" : ""}
                    {t.next_run_at && ` · next ${fmtRel(t.next_run_at)}`}{t.last_run_at && ` · last ${fmtRel(t.last_run_at)} (${t.last_status})`}</div>
                </div>
              ))}
            </div>
          )}
        </section>
        <section>
          <h3 className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">Runs</h3>
          {!runs.length ? <Empty label="No runs yet" /> : (
            <div className="space-y-2" data-testid="claude-runs">
              {runs.map((r) => (
                <div key={r._id} className="panel" data-testid={`claude-run-${r._id}`}>
                  <button className="w-full flex items-center gap-2 px-4 py-2.5 text-left" onClick={() => setOpenRun(openRun === r._id ? null : r._id)}>
                    <span className={`pill ${r.status === "done" ? "pill-ok" : r.status === "failed" ? "pill-breach" : "pill-warn"}`}>{r.status}</span>
                    <span className="text-sm flex-1 truncate">{r.task_name}</span>
                    <span className="text-[11px] text-neutral-500">{r.trigger} · {fmtDateTime(r.started_at)}</span>
                  </button>
                  {openRun === r._id && (
                    <div className="px-4 pb-3 border-t border-[var(--border)] pt-3">
                      {r.error && <div className="text-xs text-[var(--breach)] mb-2 break-words">{r.error}</div>}
                      {r.slack_error && <div className="text-xs text-[var(--warn)] mb-2">Slack: {r.slack_error}</div>}
                      <div className="text-sm text-neutral-200 whitespace-pre-wrap break-words" data-testid="claude-run-output">{r.output || (r.status === "running" ? "Running…" : "—")}</div>
                      {r.usage?.output_tokens && <div className="text-[10px] text-neutral-600 mt-2">{r.usage.input_tokens} in · {r.usage.output_tokens} out tokens</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      {edit && <TaskDialog task={edit} skills={skills} models={models} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
}
